import { navigateToUrl } from 'single-spa';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import Store from 'store';
import Cookies from 'js-cookie';

import { getProfile } from 'api/profile';
import { getLoyalty } from 'api/customers/loyalty';
import { getPaymentMethods } from 'utils/login';
import { isExternalRedirect } from 'mf-shared/utils/sso';
import { AUTH_TYPE } from 'mf-shared/utils/auth';
import * as clevertapLogin from 'mf-shared/clevertap/login';
import {
  checkAuthTypeFailure,
  checkAuthTypeSuccess,
  authenticateGoAuthFailure,
  authenticateGoAuthSuccess,
  authenticateMokaSuccess,
  authenticateMokaFailure,
} from './actions';
import {
  AUTH_TYPE_CHECK,
  GO_AUTH_AUTHENTICATE,
  MOKA_AUTHENTICATE,
} from './types';
import {
  getAuthType,
  loginGoAuth,
  loginMoka,
  completeUserAssociationApi,
} from './api';
import { getBusinessTypes, getBusinessCategoriesAll } from '../../api/auth';
import { error as errorNotification } from '../notifications/actions';

export function* authTypeCheckSaga(action) {
  try {
    const { email, phoneNumber } = action.payload;

    const { data } = yield call(getAuthType, email, phoneNumber);
    const { authentication, notify_user_association, moka, hermes } = data;

    yield put(
      checkAuthTypeSuccess(authentication, notify_user_association, {
        moka,
        hermes,
      })
    );
  } catch (error) {
    yield put(checkAuthTypeFailure(error));
  }
}

async function afterAuthenticate(config) {
  const {
    authType,
    accessToken,
    refreshToken,
    expiry,
    redirectTo,
    isNewlyAssociated = false,
  } = config;

  // Persist auth information to local storage
  Store.set('moka:authType', authType);
  Store.set('moka:accessToken', accessToken);
  Store.set('moka:accessTokenExpiry', expiry);
  Store.set('moka:refreshToken', refreshToken);

  // BO-453 Exclusively for GoFood Middleware UI
  const tokenForGofood =
    authType === AUTH_TYPE.GO_AUTH ? `Bearer ${accessToken}` : accessToken;
  const expiryForGofood = new Date(expiry);
  const cookieSettingForGofood = {
    expires: expiryForGofood,
    domain: '.mokapos.com',
  };
  Cookies.set('moka:authType', authType, cookieSettingForGofood);
  Cookies.set('moka:accessToken', tokenForGofood, cookieSettingForGofood);
  Cookies.set('remember_token', tokenForGofood, cookieSettingForGofood);

  const { data: profile } = await getProfile();
  Store.set('response:login', profile);

  // Allow user to see billing discount banner after login
  Store.remove('hasCloseBillingDiscountBanner');

  // Missing role information in profile
  const role = {};
  // store.set('response:user_role_name', role.name);

  // Setting Clevertap profile
  const [
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'data' does not exist on type '() => Prom... Remove this comment to see the full error message
    { data: businessTypes },
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'data' does not exist on type '() => Prom... Remove this comment to see the full error message
    { data: businessCategories },
  ] = await Promise.all([getBusinessTypes, getBusinessCategoriesAll]);

  let loyalty = [];
  try {
    const res = await getLoyalty();
    loyalty = res.data;
  } catch (err) {
    console.error(err);
  }

  const paymentMethods = await getPaymentMethods(profile.permissions);

  clevertapLogin.setUserProfile({
    profile,
    businessTypes,
    businessCategories,
    loyalty,
    paymentMethods,
  });
  clevertapLogin.login({ profile, role });

  if (isNewlyAssociated) {
    await completeUserAssociationApi();
  }

  // Redirect user accordingly
  if (redirectTo) {
    if (isExternalRedirect(redirectTo)) {
      navigateToUrl('/');
    } else if (redirectTo === 'gostore-merchant') {
      window.location.href = `${process.env.GOSTORE_AUTH_URL}?token=${accessToken}`;
    } else {
      navigateToUrl(redirectTo);
    }
  } else {
    navigateToUrl('/dashboard');
  }
}

export function* goAuthAuthenticateSaga(action) {
  try {
    const { email, password, redirectTo } = action.payload;
    const { accessToken, refreshToken, expiry } = yield call(
      loginGoAuth,
      email,
      password
    );

    yield put(authenticateGoAuthSuccess(accessToken, refreshToken, expiry));

    const isNewlyAssociated = yield select(
      (state) => (state as any).login.isNewlyAssociated
    );

    yield call(afterAuthenticate, {
      authType: AUTH_TYPE.GO_AUTH,
      accessToken,
      refreshToken,
      expiry,
      redirectTo,
      isNewlyAssociated,
    });
  } catch (error) {
    yield put(authenticateGoAuthFailure(error));
    yield put(errorNotification(error.message));
  }
}

export function* mokaAuthenticateSaga(action) {
  try {
    const { email, phone, password, redirectTo } = action.payload;
    const { accessToken, refreshToken, expiry } = yield call(
      loginMoka,
      email,
      phone,
      password
    );

    yield put(authenticateMokaSuccess(accessToken, refreshToken, expiry));
    yield call(afterAuthenticate, {
      authType: AUTH_TYPE.MOKA,
      accessToken,
      refreshToken,
      expiry,
      redirectTo,
    });
  } catch (error) {
    yield put(authenticateMokaFailure(error));
    yield put(errorNotification(error.message));
  }
}

export default function* authSaga() {
  yield takeEvery(AUTH_TYPE_CHECK, authTypeCheckSaga);
  yield takeEvery(GO_AUTH_AUTHENTICATE, goAuthAuthenticateSaga);
  yield takeEvery(MOKA_AUTHENTICATE, mokaAuthenticateSaga);
}
